import React, { Fragment, useState, useEffect, useMemo, useCallback, memo } from "react";
import PropTypes from 'prop-types';
import CrewComponent from "../crew/CrewComponent";
import CommentComponent from "../comment/CommentComponent";
import "./DetailsComponent.scss";
import SetRoles2 from "./setRoles2";

/**
 * Optimized DetailsComponent for displaying task details with crew information
 * @param {Object} props - Component props
 * @param {Array} props.fsCrewList - Flight service crew list
 * @param {Array} props.csCrewList - Customer service crew list
 * @param {Object} props.previousTask - Previous task information
 * @param {string} props.startLocation - Task start location
 * @param {string} props.endLocation - Task end location
 * @param {Object} props.selectedTaskBar - Selected task bar information
 * @param {Object} props.loginAsData - Login data
 * @param {boolean} props.crewChief - Flag indicating if user is crew chief
 * @param {boolean} props.ccRoleEnabled - Flag for crew chief role enabled
 * @param {boolean} props.myFlights - Flag for MyFlights mode
 * @param {string} props.responsibility - User responsibility (CS/FS)
 * @param {string} props.empName - Employee name
 * @param {boolean} props.showNameFlag - Flag to show names
 * @param {boolean} props.isSingleAgent - Flag for single agent mode
 * @param {Object} props.decodedToken - Decoded authentication token
 * @param {Object} props.shiftInfoList - Shift information
 * @param {Object} props.fetchDetailsParams - Parameters for fetching details
 * @param {Array} props.commentsList - List of comments
 * @param {Array} props.taskRoles - Available task roles
 * @param {number} props.setRolesCount - Roles count
 * @param {Array} props.allowedTaskRoles - Allowed task roles
 * @param {Object} props.action - Action creators
 * @param {Function} props.callForDetailsAction - Function to call for details
 * @param {Function} props.callForAllUserDetails - Function to call for all user details
 * @returns {JSX.Element} Rendered details component
 */
const DetailsComponent = memo((props) => {
  const [modalShowFlag, setModalShowFlag] = useState(false);
  const [isRolesButtonShowable, setIsRolesButtonShowable] = useState(false);

  // Memoized callback for modal show/hide functionality
  const showHideModal = useCallback((val) => {
    if (!isRolesButtonShowable) {
      alert("No crew members available to assign them roles.");
      return;
    }
    
    setModalShowFlag(
      val != null && typeof val === "boolean" ? val : !modalShowFlag
    );
  }, [isRolesButtonShowable, modalShowFlag]);

  // Memoized check for roles button visibility
  const shouldShowRolesButton = useMemo(() => {
    return (
      props.selectedTaskBar?.value === "home" &&
      !props.loginAsData?.userId &&
      props.crewChief === true &&
      isRolesButtonShowable === true &&
      props.ccRoleEnabled === true
    );
  }, [
    props.selectedTaskBar?.value,
    props.loginAsData?.userId,
    props.crewChief,
    isRolesButtonShowable,
    props.ccRoleEnabled
  ]);

  // Memoized filtered crew list for roles
  const filteredFsCrewList = useMemo(() => {
    if (props.fsCrewList != null && props.fsCrewList.length > 0) {
      return props.fsCrewList.filter((el) => el.taskRoleEligible === true);
    }
    return [];
  }, [props.fsCrewList]);

  // Memoized location display check
  const shouldShowLocationHeader = useMemo(() => {
    return (
      props.endLocation && 
      props.startLocation && 
      props.endLocation !== props.startLocation
    );
  }, [props.endLocation, props.startLocation]);

  // Effect for processing fsCrewList and setting roles button visibility
  useEffect(() => {
    if (props.fsCrewList != null) {
      // Add previousTask to each crew member (direct mutation like original)
      props.fsCrewList.forEach((item) => {
        item.previousTask = props.previousTask;
      });

      // Check if all crew members require acknowledgment
      const allRequireAck = props.fsCrewList.every(
        (item) => item.ackRequired === true
      );
      
      setIsRolesButtonShowable(!allRequireAck);
    }
  }, [props.fsCrewList, props.previousTask]);

  // Effect for processing csCrewList
  useEffect(() => {
    if (props.csCrewList !== null && props.csCrewList !== undefined) {
      // Add previousTask to each crew member (immutable update)
      props.csCrewList.forEach((item) => {
        item.previousTask = props.previousTask;
      });
    }
  }, [props.csCrewList, props.previousTask]);

  // Memoized crew component props for CS crew
  const csCrewProps = useMemo(() => ({
    crewList: props.csCrewList,
    crewInfo: "care",
    empName: props.empName,
    showNameFlag: props.showNameFlag,
    isCrewChief: props.crewChief,
    role: props.decodedToken?.SM_ROLE,
    shiftInfoList: null,
    fetchDetailsParams: props.fetchDetailsParams,
    isSingleAgent: props.isSingleAgent,
  }), [
    props.csCrewList,
    props.empName,
    props.showNameFlag,
    props.crewChief,
    props.decodedToken?.SM_ROLE,
    props.fetchDetailsParams,
    props.isSingleAgent
  ]);

  // Memoized crew component props for FS crew
  const fsCrewProps = useMemo(() => ({
    crewList: props.fsCrewList,
    crewInfo: "operations",
    empName: props.empName,
    showNameFlag: props.showNameFlag,
    isCrewChief: props.crewChief,
    role: props.decodedToken?.SM_ROLE,
    shiftInfoList: props.shiftInfoList,
    fetchDetailsParams: props.fetchDetailsParams,
    isSingleAgent: props.isSingleAgent,
  }), [
    props.fsCrewList,
    props.empName,
    props.showNameFlag,
    props.crewChief,
    props.decodedToken?.SM_ROLE,
    props.shiftInfoList,
    props.fetchDetailsParams,
    props.isSingleAgent
  ]);

  // Memoized roles button styles
  const rolesButtonStyles = useMemo(() => ({
    marginLeft: "22px",
    marginBottom: "6px",
  }), []);

  // Memoized SetRoles2 props
  const setRolesProps = useMemo(() => ({
    modalShowFlag,
    showHideModal,
    fsCrewList: filteredFsCrewList,
    superprops: props,
    action: props.action,
    taskRoles: props.taskRoles,
    fetchDetailsParams: props.fetchDetailsParams,
    callForDetailsAction: props.callForDetailsAction,
    setRolesCount: props.setRolesCount,
    callForAllUserDetails: props.callForAllUserDetails,
    allowedTaskRoles: props.allowedTaskRoles,
  }), [
    modalShowFlag,
    showHideModal,
    filteredFsCrewList,
    props
  ]);

  return (
    <Fragment>
      <div className="details">
        {/* Task End Location Section */}
        {shouldShowLocationHeader && (
          <div className="endLocation">
            <div className="locationheader mb-3">Task End Location</div>
            <div className="locationheader mb-3">{props.endLocation}</div>
          </div>
        )}

        {/* Task Details Header */}
        <div className="header1 mb-3">Task Details</div>
        <div className="header2">Crew</div>

        {/* Roles Button */}
        {shouldShowRolesButton && (
          <button
            onClick={showHideModal}
            style={rolesButtonStyles}
            className="rolesBtn rolesBtnSubmit btn btn-primary"
            type="button"
            aria-label="Assign roles to crew members"
          >
            Roles
          </button>
        )}

        {/* SetRoles Modal */}
        {modalShowFlag && <SetRoles2 {...setRolesProps} />}

        {/* Crew Components based on MyFlights mode and responsibility */}
        {props.myFlights && props.responsibility === "CS" ? (
          <div className="row mx-0">
            <div className="col-sm-12">
              <CrewComponent {...csCrewProps} />
            </div>
          </div>
        ) : props.myFlights && props.responsibility === "FS" ? (
          <div className="row mx-0">
            <div className="col-sm-12">
              <CrewComponent {...fsCrewProps} />
            </div>
          </div>
        ) : (
          <div className="row mx-0">
            <div className="col-sm-6">
              <CrewComponent {...csCrewProps} />
            </div>
            <div className="col-sm-6">
              <CrewComponent {...fsCrewProps} />
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="row mx-0">
          <div className="col-sm-12">
            <hr />
          </div>
        </div>

        {/* Comments Section - Only shown when not in MyFlights mode */}
        {!props.myFlights && (
          <div className="row mb-2 mx-0">
            <div className="col-sm-12">
              <CommentComponent commentsList={props.commentsList} />
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
});

// Display name for debugging
DetailsComponent.displayName = 'DetailsComponent';

// PropTypes for type checking
DetailsComponent.propTypes = {
  fsCrewList: PropTypes.arrayOf(PropTypes.shape({
    taskRoleEligible: PropTypes.bool,
    ackRequired: PropTypes.bool,
  })),
  csCrewList: PropTypes.array,
  previousTask: PropTypes.object,
  startLocation: PropTypes.string,
  endLocation: PropTypes.string,
  selectedTaskBar: PropTypes.shape({
    value: PropTypes.string,
  }),
  loginAsData: PropTypes.shape({
    userId: PropTypes.string,
  }),
  crewChief: PropTypes.bool,
  ccRoleEnabled: PropTypes.bool,
  myFlights: PropTypes.bool,
  responsibility: PropTypes.oneOf(['CS', 'FS']),
  empName: PropTypes.string,
  showNameFlag: PropTypes.bool,
  isSingleAgent: PropTypes.bool,
  decodedToken: PropTypes.shape({
    SM_ROLE: PropTypes.string,
  }),
  shiftInfoList: PropTypes.object,
  fetchDetailsParams: PropTypes.object,
  commentsList: PropTypes.array,
  taskRoles: PropTypes.array,
  setRolesCount: PropTypes.number,
  allowedTaskRoles: PropTypes.array,
  action: PropTypes.object,
  callForDetailsAction: PropTypes.func,
  callForAllUserDetails: PropTypes.func,
};

// Default props
DetailsComponent.defaultProps = {
  fsCrewList: [],
  csCrewList: [],
  crewChief: false,
  ccRoleEnabled: false,
  myFlights: false,
  showNameFlag: false,
  isSingleAgent: false,
  commentsList: [],
  taskRoles: [],
  allowedTaskRoles: [],
  setRolesCount: 0,
};

export default DetailsComponent;
